import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth-context";
import { Pill } from "../components/Pill";
import * as shared from "@ventrin/shared";
type ActivePlan = "early_access" | "starter" | "team" | "enterprise";
const resolvePlan = (p: any) => shared.resolvePlan(p);

interface BillingConfigPlan {
  id: string;
  name: string;
  priceDisplay: string;
  maxUsers: number;
  selfServe: boolean;
  hasPriceId: boolean;
  features: string[];
}

const FEATURE_LABELS: Record<string, string> = {
  browser_protection: "Browser-first prompt protection",
  local_detection: "Local-first detection and sanitisation",
  standard_policies: "Standard policy templates",
  event_log: "Full event log and admin dashboard",
  admin_dashboard: "Admin dashboard",
  custom_policies: "Custom policies by team and tool",
  document_upload_protection: "Document upload protection",
  false_flag_review: "False-flag review workflow",
  team_management: "Team management",
  sso: "SSO and SCIM provisioning",
  scim: "SCIM",
  custom_packs: "Custom rule packs and vertical policies",
  self_hosted_verifier: "Optional self-hosted policy verifier",
};

const TOOLS_LINE = "ChatGPT, Claude, Gemini, Copilot";

export function BillingPage() {
  const { profile } = useAuth();
  const location = useLocation();
  const [config, setConfig] = useState<{ configured: boolean; plans: BillingConfigPlan[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState<ActivePlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const currentPlan: ActivePlan = resolvePlan(profile.workspace?.plan);
  const currentIndex = shared.PLAN_ORDER.indexOf(currentPlan);
  const subStatus = profile.workspace?.billing?.status ?? "none";
  const customerId = profile.workspace?.billing?.customerId;
  const renewalAt = profile.workspace?.billing?.currentPeriodEnd ?? profile.workspace?.renewalAt;
  const cancelAtPeriodEnd = !!profile.workspace?.billing?.cancelAtPeriodEnd;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getBillingConfig()
      .then((res) => { if (!cancelled) setConfig(res); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? "Could not load billing."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Surface checkout success/cancel hints via the URL params Stripe redirects with.
  useEffect(() => {
    const params = new URLSearchParams(location.search || (location.hash.split("?")[1] ?? ""));
    const status = params.get("status");
    if (status === "success") setNotice("Checkout completed. Your plan will activate as soon as we receive Stripe's confirmation.");
    if (status === "cancel") setNotice("Checkout cancelled. You can pick another plan whenever you're ready.");
  }, [location.search, location.hash]);

  async function startCheckout(plan: "starter" | "team") {
    setError(null);
    setBusyPlan(plan);
    try {
      const res = await api.createCheckoutSession({ plan });
      window.location.href = res.url;
    } catch (e: any) {
      const code = e?.details?.code ?? e?.code;
      if (code === "billing-not-configured") {
        setError("Billing isn't configured yet. The Stripe secret key hasn't been set on the server.");
      } else {
        setError(e?.message ?? "Could not start checkout.");
      }
    } finally {
      setBusyPlan(null);
    }
  }

  async function openPortal() {
    setError(null);
    try {
      const res = await api.createPortalSession();
      window.location.href = res.url;
    } catch (e: any) {
      const code = e?.details?.code ?? e?.code;
      if (code === "billing-not-configured") {
        setError("Billing isn't configured yet.");
      } else {
        setError(e?.message ?? "Could not open the billing portal.");
      }
    }
  }

  // Use server-reported plan definitions if available (because they reflect
  // env-resolved price IDs); otherwise fall back to the shared module.
  const plans = useMemo<BillingConfigPlan[]>(() => {
    if (config?.plans?.length) return config.plans;
    return shared.PLAN_ORDER.map((p) => {
      const def = shared.PLANS[p];
      return {
        id: def.id,
        name: def.name,
        priceDisplay: def.priceDisplay,
        maxUsers: Number.isFinite(def.maxUsers) ? def.maxUsers : -1,
        selfServe: def.selfServe,
        hasPriceId: false,
        features: [...def.features],
      };
    });
  }, [config]);

  return (
    <>
      <header className="sg-page-header">
        <div>
          <h1 className="sg-page-title">Billing</h1>
          <div className="sg-page-sub">Manage your subscription, change plan, view seat usage.</div>
        </div>
        <div className="sg-row" style={{ gap: 10, alignItems: "center" }}>
          <Pill kind="neutral">{shared.PLANS[currentPlan].name}</Pill>
          <Pill kind={subStatus === "active" || subStatus === "trialing" ? "allow" : subStatus === "past_due" ? "warn" : "neutral"}>
            {subStatusLabel(subStatus)}
          </Pill>
        </div>
      </header>

      {error && (
        <div className="sg-panel" style={{ marginBottom: 16, borderLeft: "3px solid var(--vt-red, #b3261a)" }}>
          <div className="sg-panel-pad">{error}</div>
        </div>
      )}
      {notice && (
        <div className="sg-panel" style={{ marginBottom: 16 }}>
          <div className="sg-panel-pad">{notice}</div>
        </div>
      )}

      {config && !config.configured && (
        <div className="sg-panel" style={{ marginBottom: 16, borderLeft: "3px solid var(--vt-amber, #b3791a)" }}>
          <div className="sg-panel-pad">
            <strong>Billing isn't configured yet.</strong>{" "}
            Set <code>STRIPE_SECRET_KEY</code>, <code>STRIPE_WEBHOOK_SECRET</code>, and the per-plan
            price IDs in the functions environment to enable purchases. The plan cards below show what
            each tier unlocks.
          </div>
        </div>
      )}

      <div className="sg-panel" style={{ marginBottom: 16 }}>
        <div className="sg-panel-head"><div className="sg-panel-title">Current plan</div></div>
        <div className="sg-panel-pad sg-stack" style={{ gap: 8 }}>
          <div><strong>{shared.PLANS[currentPlan].name}</strong> — {shared.PLANS[currentPlan].priceDisplay}</div>
          <div className="sg-muted" style={{ fontSize: 12 }}>
            Up to {Number.isFinite(shared.PLANS[currentPlan].maxUsers) ? shared.PLANS[currentPlan].maxUsers : "unlimited"} users.
            {renewalAt ? ` Renews ${new Date(renewalAt).toLocaleDateString()}.` : ""}
            {cancelAtPeriodEnd ? " Set to cancel at period end." : ""}
          </div>
          {customerId && (
            <div>
              <button className="sg-btn" onClick={openPortal}>
                Manage subscription
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="sg-grid sg-grid-2" style={{ gap: 16 }}>
        {plans.map((p) => {
          const planId = p.id as ActivePlan;
          const idx = shared.PLAN_ORDER.indexOf(planId);
          const isCurrent = planId === currentPlan;
          const isUpgrade = idx > currentIndex;
          const isDowngrade = idx < currentIndex && idx >= 0;
          return (
            <div key={p.id} className="sg-panel" style={isCurrent ? { borderLeft: "3px solid var(--vt-blue, #2c5de5)" } : undefined}>
              <div className="sg-panel-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div className="sg-panel-title">{p.name}</div>
                <div style={{ fontSize: 12, color: "var(--vt-ink-3, #6a6a6a)" }}>{p.priceDisplay}</div>
              </div>
              <div className="sg-panel-pad sg-stack" style={{ gap: 8 }}>
                <div className="sg-muted" style={{ fontSize: 12 }}>
                  Up to {p.maxUsers === -1 ? "unlimited" : p.maxUsers} users · {TOOLS_LINE}
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  {dedupeFeatures(p.features).map((f) => (
                    <li key={f}>{FEATURE_LABELS[f] ?? f}</li>
                  ))}
                </ul>
                <div className="sg-row" style={{ gap: 8, marginTop: 4 }}>
                  {isCurrent && <Pill kind="allow">Current plan</Pill>}
                  {!isCurrent && p.selfServe && p.hasPriceId && (planId === "starter" || planId === "team") && (
                    <button
                      className="sg-btn sg-btn-primary"
                      disabled={busyPlan === planId}
                      onClick={() => startCheckout(planId as "starter" | "team")}
                    >
                      {busyPlan === planId ? "Redirecting…" : isUpgrade ? "Upgrade" : isDowngrade ? "Downgrade" : "Choose"}
                    </button>
                  )}
                  {!isCurrent && !p.selfServe && planId === "enterprise" && (
                    <a className="sg-btn" href="mailto:sales@ventrin.com?subject=Enterprise%20enquiry">
                      Contact sales
                    </a>
                  )}
                  {!isCurrent && p.selfServe && !p.hasPriceId && (
                    <span className="sg-muted" style={{ fontSize: 12 }}>
                      Price ID not configured yet
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {loading && <div className="sg-empty" style={{ marginTop: 16 }}>Loading billing config…</div>}
    </>
  );
}

function dedupeFeatures(features: string[]): string[] {
  // The entitlement list naturally has both "event_log" and "admin_dashboard"
  // as separate keys; the pricing copy folds them into one bullet.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of features) {
    if (f === "admin_dashboard" && seen.has("event_log")) continue; // covered by the merged line
    if (!seen.has(f)) { seen.add(f); out.push(f); }
  }
  return out;
}

function subStatusLabel(s: string): string {
  switch (s) {
    case "active": return "Active";
    case "trialing": return "Trial";
    case "past_due": return "Past due";
    case "canceled": return "Cancelled";
    case "incomplete":
    case "incomplete_expired": return "Incomplete";
    case "unpaid": return "Unpaid";
    default: return "No subscription";
  }
}
